import { ComponentType, lazy } from 'react';
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
} from 'react-router-dom';
import { Login } from '../pages/Login/Login';
import { NotFound } from '../pages/NotFound';
import { useAuth } from '../hooks/auth';
import { Theme } from '../components/Theme/Theme';
import { FormProvider } from '../contexts/FormContext';
import { LazyPage } from '../components/lazy-page';

const Painel = lazy(() =>
  import('../pages/Painel/Painel').then((m) => ({ default: m.Painel }))
);
const Regulamento = lazy(() =>
  import('../pages/Regulamento/Regulamento').then((m) => ({
    default: m.Regulamento,
  }))
);
const Propriedades = lazy(() =>
  import('../pages/Propriedades/Propriedades').then((m) => ({
    default: m.Propriedades,
  }))
);
const Cupons = lazy(() =>
  import('../pages/Cupons/Cupons').then((m) => ({ default: m.Cupons }))
);
const PropriedadeView = lazy(() =>
  import('../pages/PropriedadeView/PropriedadeView').then((m) => ({
    default: m.PropriedadeView,
  }))
);
const Usuarios = lazy(() =>
  import('../pages/Usuarios/Usuarios').then((m) => ({ default: m.Usuarios }))
);
const Vistorias = lazy(() =>
  import('../pages/Vistorias/Vistorias').then((m) => ({ default: m.Vistorias }))
);
const Settings = lazy(() =>
  import('../pages/Settings').then((m) => ({ default: m.Settings }))
);
const ProdutoresEmpresas = lazy(() =>
  import('../pages/ProdutoresEmpresas/ProdutoresEmpresas').then((m) => ({
    default: m.ProdutoresEmpresas,
  }))
);
const CadastroListas = lazy(() =>
  import('../pages/CadastroListas/CadastroListas').then((m) => ({
    default: m.CadastroListas,
  }))
);
const Hibridos = lazy(() =>
  import('../pages/Hibridos').then((m) => ({ default: m.Hibridos }))
);
const Defensivos = lazy(() =>
  import('../pages/Defensivos').then((m) => ({ default: m.Defensivos }))
);
const Inoculantes = lazy(() =>
  import('../pages/Inoculantes').then((m) => ({ default: m.Inoculantes }))
);
const Bioestimulantes = lazy(() =>
  import('../pages/Bioestimulantes').then((m) => ({
    default: m.Bioestimulantes,
  }))
);
const PropriedadesComCar = lazy(() =>
  import('../pages/PropriedadesComCar').then((m) => ({
    default: m.PropriedadesComCar,
  }))
);
const FabricanteMaquina = lazy(() =>
  import('../pages/FabricanteMaquina').then((m) => ({
    default: m.FabricanteMaquina,
  }))
);
const FabricanteFertilizante = lazy(() =>
  import('../pages/FabricanteFertilizante').then((m) => ({
    default: m.FabricanteFertilizante,
  }))
);
const FabricanteDrone = lazy(() =>
  import('../pages/FabricanteDrone').then((m) => ({
    default: m.FabricanteDrone,
  }))
);
const ModeloDrone = lazy(() =>
  import('../pages/ModeloDrone').then((m) => ({ default: m.ModeloDrone }))
);
const FabricanteMeteorologia = lazy(() =>
  import('../pages/FabricanteMeteorologia').then((m) => ({
    default: m.FabricanteMeteorologia,
  }))
);
const FabricanteIrrigacao = lazy(() =>
  import('../pages/FabricanteIrrigacao').then((m) => ({
    default: m.FabricanteIrrigacao,
  }))
);
const Rtvs = lazy(() =>
  import('../pages/Rtvs/Rtvs').then((m) => ({ default: m.Rtvs }))
);
const Auditoria = lazy(() =>
  import('../pages/Auditoria').then((m) => ({ default: m.Auditoria }))
);

function Auth({ children }: { children: JSX.Element }) {
  const { user } = useAuth();
  const location = useLocation();

  if (!user) {
    return <Navigate to='/' state={{ from: location }} replace />;
  }
  return children;
}

const withAuthTheme = (Page: ComponentType) => (
  <Auth>
    <Theme>
      <LazyPage>
        <Page />
      </LazyPage>
    </Theme>
  </Auth>
);

export const RoutesApp: React.FC = () => {
  return (
    <BrowserRouter>
      <FormProvider>
        <Routes>
          <Route path='/' element={<Login />} />
          <Route path='/painel' element={withAuthTheme(Painel)} />
          <Route path='/regulamento' element={withAuthTheme(Regulamento)} />
          <Route path='/hibridos' element={withAuthTheme(Hibridos)} />
          <Route path='/defensivos' element={withAuthTheme(Defensivos)} />
          <Route path='/inoculantes' element={withAuthTheme(Inoculantes)} />
          <Route
            path='/bioestimulantes'
            element={withAuthTheme(Bioestimulantes)}
          />
          <Route
            path='/propriedades-com-car'
            element={withAuthTheme(PropriedadesComCar)}
          />
          <Route path='/fabricantes' element={withAuthTheme(FabricanteMaquina)} />
          <Route
            path='/fabricantesFertilizantes'
            element={withAuthTheme(FabricanteFertilizante)}
          />
          <Route
            path='/fabricantesDrone'
            element={withAuthTheme(FabricanteDrone)}
          />
          <Route path='/modelosDrone' element={withAuthTheme(ModeloDrone)} />
          <Route
            path='/fabricantesMeteorologia'
            element={withAuthTheme(FabricanteMeteorologia)}
          />
          <Route
            path='/fabricantesIrrigacao'
            element={withAuthTheme(FabricanteIrrigacao)}
          />
          <Route path='/cupons' element={withAuthTheme(Cupons)} />
          <Route path='/propriedades' element={withAuthTheme(Propriedades)} />
          <Route path='/usuarios' element={withAuthTheme(Usuarios)} />
          <Route path='/auditoria' element={withAuthTheme(Auditoria)} />
          <Route path='/configuracoes' element={withAuthTheme(Settings)} />
          <Route path='/vistorias' element={withAuthTheme(Vistorias)} />
          <Route
            path='/propriedades/view/:id'
            element={withAuthTheme(PropriedadeView)}
          />
          <Route
            path='/produtoresEmpresas'
            element={withAuthTheme(ProdutoresEmpresas)}
          />
          <Route path='/rtvs' element={withAuthTheme(Rtvs)} />
          <Route
            path='/cadastroListas'
            element={withAuthTheme(CadastroListas)}
          />
          <Route path='*' element={<NotFound />} />
        </Routes>
      </FormProvider>
    </BrowserRouter>
  );
};
